import { useEffect, useState } from 'react'
import { UseFormWatch } from 'react-hook-form'

import { Box, Stack, Typography } from '@mui/material'
import { DataService } from 'api/DataService'
import { useAuthContext } from 'context/AuthContext'
import { constructorProducts } from 'models/Plans'
import moment from 'moment'

import { ExtraFieldItem } from 'components/ExtraFieldsManager'
import Upgrade from 'components/Upgrade'

import { resolveUploadSettings, UploadSettings } from 'utils/customFieldUpload'
import { getPortalBaseUrl } from 'utils/domain-urls'
import { priceConverter } from 'utils/FormatMoney'

import { theme } from 'styles/theme'

import CreatePaymentLinkFormInterface, {
  ExtraField,
  PaymentLink
} from '../../interface'
import * as S from './styles'
import { generateUrl } from './utils'

// Interfaces para a nova estrutura da API v3
interface BillingItem {
  uuid: string
  description: string
  amount_billed: string
  amount_unit: string
  quantity: string
  enable_customer_select_quantity: boolean
  product?: {
    uuid: string
    name: string
    [key: string]: any
  }
}

interface CustomFieldConfig {
  uuid: string
  required: boolean
  position: number
  custom_field: {
    uuid: string
    field_type: 'short_text' | 'long_text' | 'radio' | 'checkbox' | 'upload'
    field_configuration: {
      title: string
      [key: string]: any
    }
    upload_settings?: UploadSettings
  }
}

// Chaves de upload do field_configuration (tipos aceitos, vários arquivos e
// limite), já com os padrões aplicados
const uploadConfiguration = (field: ExtraFieldItem) => {
  const isUpload = field.field_type === 'upload' || field.field_type === 4
  if (!isUpload) return {}

  const settings = resolveUploadSettings(field)

  return {
    accepted_types: settings.accepted_types,
    multiple: settings.multiple,
    ...(settings.multiple ? { max_files: settings.max_files } : {})
  }
}

type CreatePaymentLinkPreviewProps = {
  watch: UseFormWatch<CreatePaymentLinkFormInterface>
  extraFields: ExtraField[] | ExtraFieldItem[]
  isMobile: boolean
  setMobileTabToForm?: () => void
  paymentLink?: PaymentLink
}

interface PreviewMerchantData {
  primary_color?: string
  font_color?: string
  logo?: string
}

const CreatePaymentLinkPreview: React.FC<CreatePaymentLinkPreviewProps> = ({
  watch,
  extraFields
}: CreatePaymentLinkPreviewProps) => {
  const { user, capabilities } = useAuthContext()
  const [merchantData, setMerchantData] = useState<PreviewMerchantData>({})

  const personalizedDomainAccess =
    capabilities?.['product_link.personalized_url']

  const formData = watch()
  const { title, description, productUuid } = formData

  const [previewUrls, setPreviewUrls] = useState({
    dataUrl: '',
    paymentUrl: ''
  })

  const SCALE_FACTOR = 0.8

  const generatePreview = async () => {
    if (!merchantData?.primary_color) return

    const formData = watch()
    const { items } = formData

    const baseData = {
      title: title || '',
      description: description || '',
      due_date: moment().format('DD/MM/yyyy'),
      primary_color: merchantData.primary_color || '',
      secondary_color: merchantData.font_color || '',
      payment_type: 'pix',
      extra_fields: extraFields.map((field) => field.name).toString() || '',
      product_amount: '0.00',
      product_name: '',
      product_icon: '',
      interest_fee: '',
      interest_fine: '',
      discounts_days_1: 'undefined',
      discounts_amount_1: 'undefined',
      discounts_days_2: 'undefined',
      discounts_amount_2: 'undefined',
      interest_policy: 'no_interest',
      product_due_date: moment().format('DD/MM/yyyy'),
      image: '',
      product_description: '',
      product_uuid: '',
      // Novos campos para API v3
      billing_items: '',
      custom_field_configs: ''
    }

    // Processar billing_items do formulário
    if (items && items.length > 0) {
      const billingItems: BillingItem[] = items.map((item) => {
        // Converter o preço formatado para número usando priceConverter
        const priceAsNumber = priceConverter(item.price || 0)
        const quantity = Number(item.quantity || 1)

        return {
          uuid: item.uuid,
          description: item.description || '',
          amount_billed: String(priceAsNumber * quantity),
          amount_unit: String(priceAsNumber),
          quantity: String(quantity),
          enable_customer_select_quantity:
            item.enable_customer_select_quantity || false,
          product:
            item.product?.value && item.product.value !== 'custom'
              ? {
                  uuid: item.product.value,
                  name: item.product.label || ''
                }
              : undefined
        }
      })

      baseData.billing_items = JSON.stringify(billingItems)

      // Usar dados do primeiro item para compatibilidade com preview
      const firstItem = items[0]
      if (firstItem) {
        // Converter o preço formatado para número usando priceConverter
        const firstItemPrice = priceConverter(firstItem.price || 0)
        const firstItemQuantity = Number(firstItem.quantity || 1)

        baseData.product_amount = String(firstItemPrice * firstItemQuantity)
        baseData.product_name =
          firstItem.description || firstItem.product?.label || ''
        baseData.product_description = firstItem.description || ''
        baseData.product_uuid = firstItem.uuid
      }
    }
    // Processar custom_field_configs se disponível
    if (extraFields && extraFields.length > 0) {
      // Type guard para verificar se é ExtraFieldItem (tem field_type)
      const isExtraFieldItem = (
        field: ExtraField | ExtraFieldItem
      ): field is ExtraFieldItem => {
        return 'field_type' in field
      }

      const customFieldConfigs: CustomFieldConfig[] = (
        extraFields as (ExtraField | ExtraFieldItem)[]
      )
        .filter(isExtraFieldItem)
        .map((field, index) => ({
          uuid: field.uuid,
          required: field.required || false,
          position: index + 1,
          custom_field: {
            uuid: field.uuid,
            field_type: (field.field_type as any) || 'short_text',
            field_configuration: {
              title: field.name,
              // SC-11348: o preview do upload mostra tipos aceitos e limites
              ...uploadConfiguration(field)
            },
            ...(field.upload_settings
              ? { upload_settings: field.upload_settings }
              : {})
          }
        }))

      if (customFieldConfigs.length > 0) {
        baseData.custom_field_configs = JSON.stringify(customFieldConfigs)
      }
    }

    // Buscar dados de produto individual se necessário (para compatibilidade com estrutura legada)
    if (
      productUuid &&
      productUuid !== 'initial' &&
      (!items || items.length === 0)
    ) {
      try {
        const response = await DataService({
          type: 'GET',
          url: `/v2/products/${productUuid}`
        })

        const product = constructorProducts([response.data.record])[0]

        if (product) {
          baseData.product_amount = product.amount
            ? String(product.amount)
            : '0.00'
          baseData.product_name = product.name || ''
          baseData.product_icon = product.icon || ''
          baseData.interest_fee = product.interestFee
            ? String(product.interestFee)
            : ''
          baseData.interest_fine = product.interestFine
            ? String(product.interestFine)
            : ''
          baseData.discounts_days_1 =
            product.discounts?.[0]?.days?.toString() || 'undefined'
          baseData.discounts_amount_1 = product.discounts?.[0]?.amount
            ? String(product.discounts[0].amount)
            : 'undefined'
          baseData.discounts_days_2 =
            product.discounts?.[1]?.days?.toString() || 'undefined'
          baseData.discounts_amount_2 = product.discounts?.[1]?.amount
            ? String(product.discounts[1].amount)
            : 'undefined'
          baseData.interest_policy = product.interestPolicy || 'no_interest'
          baseData.image = product.documentFile?.thumbnail_url || ''
          baseData.product_description = product.description || ''
          baseData.product_uuid = product.uuid || ''
        }
      } catch (error) {
        console.error('Erro ao buscar produto:', error)
      }
    }

    const dataUrl = generateUrl(baseData, 0, merchantData, user?.name)
    const paymentUrl = generateUrl(baseData, 1, merchantData, user?.name)

    setPreviewUrls({ dataUrl, paymentUrl })
  }
  useEffect(() => {
    const getMerchantData = async () => {
      try {
        const response = await DataService({
          type: 'GET',
          url: '/v1/merchants/perfil'
        })

        setMerchantData({
          primary_color: response.data.record.primary_color,
          font_color: response.data.record.font_color,
          logo: response.data.record.logo
        })
      } catch (error) {
        console.error('Erro ao carregar dados do merchant:', error)
      }
    }

    getMerchantData()
  }, [])

  useEffect(() => {
    generatePreview()
  }, [merchantData, title, description, productUuid, extraFields, watch()])

  return (
    <S.PreviewWrapper>
      <S.PreviewContent>
        <Box
          bgcolor="#fff"
          width="100%"
          display="flex"
          flexDirection="column"
          borderRadius={2}
          height="100%"
          sx={{ position: 'relative', overflow: 'hidden' }}
        >
          <Box py={2} px={3}>
            <Stack
              py={1}
              flexDirection="row"
              justifyContent="center"
              borderRadius={99}
              gap={1}
              bgcolor={theme.palette.neutral[800]}
            >
              <Typography>
                {getPortalBaseUrl(user?.subdomain).replace('https://', '')}
              </Typography>
              {!personalizedDomainAccess?.has_access && (
                <Upgrade
                  plan={personalizedDomainAccess?.plan}
                  resourceName="Domínio personalizado"
                />
              )}
            </Stack>
          </Box>

          <div
            style={{
              width: '100%',
              height: '100%',
              overflow: 'hidden'
            }}
          >
            <iframe
              style={{
                width: `${100 / SCALE_FACTOR}%`,
                height: `${100 / SCALE_FACTOR}%`,
                border: 'none',
                transform: `scale(${SCALE_FACTOR})`,
                transformOrigin: '0 0'
              }}
              src={previewUrls.dataUrl}
            />
          </div>
        </Box>
      </S.PreviewContent>
    </S.PreviewWrapper>
  )
}

export default CreatePaymentLinkPreview
